import React from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';

const Privacy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/login" />
          </IonButtons>
          <IonTitle>Privacy Policy</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding">
        <IonCard>
          <IonCardContent>
            <div className="prose max-w-none dark:prose-invert">
              <h1 className="text-2xl font-bold mb-4">
                Privacy Policy for Tickets Manager
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
                <p className="mb-3">
                  This Privacy Policy describes how Tickets Manager ("we",
                  "our", or "the app") collects, uses, shares, and protects your
                  information when you use our ticket management application.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  2. Information We Collect
                </h2>

                <h3 className="text-lg font-medium mb-2">
                  2.1 Information from Google Account
                </h3>
                <p className="mb-3">
                  When you sign in using your Google account, we collect:
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    Basic profile information (name, email address, profile
                    picture)
                  </li>
                  <li>Google account ID</li>
                  <li>Authentication tokens necessary for sign-in</li>
                </ul>

                <h3 className="text-lg font-medium mb-2">
                  2.2 Information You Provide
                </h3>
                <ul className="list-disc pl-6 mb-3">
                  <li>Event information (event names, dates, venues)</li>
                  <li>
                    Ticket inventory details (quantities, prices, categories)
                  </li>
                  <li>
                    Sales transaction data (buyer information, sale prices,
                    dates)
                  </li>
                  <li>Business-related information you choose to store</li>
                </ul>

                <h3 className="text-lg font-medium mb-2">
                  2.3 Automatically Collected Information
                </h3>
                <ul className="list-disc pl-6 mb-3">
                  <li>Usage data and analytics</li>
                  <li>Device information</li>
                  <li>Log data</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  3. How We Use Your Information
                </h2>
                <p className="mb-3">We use the collected information to:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Authenticate and maintain your account</li>
                  <li>Provide ticket inventory management services</li>
                  <li>Track sales and generate reports</li>
                  <li>Improve our services</li>
                  <li>Communicate with you about your account</li>
                  <li>Ensure security and prevent fraud</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  4. Data Sharing and Disclosure
                </h2>

                <h3 className="text-lg font-medium mb-2 text-red-600 dark:text-red-400">
                  4.1 We DO NOT sell, rent, or trade your Google user data
                </h3>

                <h3 className="text-lg font-medium mb-2">
                  4.2 Limited Sharing
                </h3>
                <p className="mb-3">
                  We may share your information only in the following
                  circumstances:
                </p>

                <p className="mb-2">
                  <strong>a) With Your Consent</strong>
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    We will share your Google user data with third parties only
                    when you have explicitly consented to such sharing
                  </li>
                  <li>
                    You will be clearly notified before any sharing occurs
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>b) For Legal Requirements</strong>
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    To comply with applicable laws, regulations, or legal
                    processes
                  </li>
                  <li>To respond to lawful requests from public authorities</li>
                  <li>
                    To protect our rights, privacy, safety, or property, and/or
                    that of our users
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>c) Service Providers</strong>
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    We may share limited data with trusted service providers who
                    assist us in operating our application, such as:
                    <ul className="list-disc pl-6 mt-2">
                      <li>Cloud hosting providers (for data storage)</li>
                      <li>
                        Analytics services (in aggregated, non-identifiable
                        form)
                      </li>
                      <li>
                        Email service providers (for account-related
                        communications)
                      </li>
                    </ul>
                  </li>
                  <li>
                    These providers are contractually obligated to protect your
                    data and use it only for the specific services they provide
                    to us
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>d) Business Transfers</strong>
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    In the event of a merger, acquisition, or sale of assets,
                    user data may be transferred
                  </li>
                  <li>
                    You will be notified of any such change and any choices you
                    may have regarding your data
                  </li>
                </ul>

                <h3 className="text-lg font-medium mb-2">
                  4.3 Data We Never Share
                </h3>
                <p className="mb-3">We never share:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Your Google authentication credentials</li>
                  <li>Raw personal data with advertisers</li>
                  <li>Individual user data with other users without consent</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">5. Data Security</h2>
                <p className="mb-3">
                  We implement appropriate technical and organizational measures
                  to protect your Google user data against:
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Unauthorized access</li>
                  <li>Alteration</li>
                  <li>Disclosure</li>
                  <li>Destruction</li>
                </ul>
                <p className="mb-3">Security measures include:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication</li>
                  <li>Secure data storage practices</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  6. Data Retention
                </h2>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    We retain your Google user data only for as long as
                    necessary to provide our services
                  </li>
                  <li>You may request deletion of your data at any time</li>
                  <li>
                    Upon account deletion, we will remove your data within 30
                    days, except where retention is required by law
                  </li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  7. Your Rights and Choices
                </h2>
                <p className="mb-3">You have the right to:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Withdraw consent for data processing</li>
                  <li>Export your data</li>
                  <li>Opt-out of non-essential data collection</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  8. Third-Party Services
                </h2>
                <p className="mb-3">
                  Our application may contain links to third-party services. We
                  are not responsible for the privacy practices of these
                  services. We encourage you to review their privacy policies.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  9. Children's Privacy
                </h2>
                <p className="mb-3">
                  Our service is not intended for children under 13. We do not
                  knowingly collect personal information from children under 13.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  10. International Data Transfers
                </h2>
                <p className="mb-3">
                  Your information may be transferred to and processed in
                  countries other than your own. We ensure appropriate
                  safeguards are in place for such transfers in accordance with
                  applicable law.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  11. Changes to This Privacy Policy
                </h2>
                <p className="mb-3">
                  We may update this Privacy Policy from time to time. We will
                  notify you of any material changes by:
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>Posting the new Privacy Policy on this page</li>
                  <li>Updating the "Last Updated" date</li>
                  <li>
                    Sending you an email notification (for significant changes)
                  </li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  12. Google API Services User Data Policy
                </h2>
                <p className="mb-3">
                  This application complies with the Google API Services User
                  Data Policy, including the Limited Use requirements. We only
                  use Google user data for the purposes explicitly described in
                  this privacy policy and with your consent.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">13. Contact Us</h2>
                <p className="mb-3">
                  If you have questions or concerns about this Privacy Policy or
                  our data practices, please contact us at:
                </p>
                <p className="mb-3">
                  <strong>Email:</strong> contact@managetix.com
                  <br />
                  <strong>Address:</strong> 38 Crown Street, London, UK
                </p>
              </section>
            </div>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default Privacy;
